import { useEffect, useRef, useState } from 'react';
import { autosaveSubmission } from '../services/submissionService';

interface UseAutosaveProps {
  formData: Record<string, any>;
  submissionId: string;
  userId: string;
  createdAt: string; // e.g., "2025-02-20T14:30:00Z"
  isSubmitted?: boolean; // whether user pressed "Submit"
  formVersion?: string; // default "1.0" if not provided
}

interface UseAutosaveReturn {
  savingStatus: 'idle' | 'saving' | 'saved' | 'error';
  handleImmediateAutosave: () => void;
}

/**
 * Improved logging helper for consistent formatting
 */
function logAutosave(level: 'log' | 'warn' | 'error', category: string, details: Record<string, any>) {
  const logObj = {
    timestamp: new Date().toISOString(),
    category,
    ...details
  };
  
  if (level === 'error') {
    console.error(`AUTOSAVE_LOG [${category}]:`, logObj);
  } else if (level === 'warn') {
    console.warn(`AUTOSAVE_LOG [${category}]:`, logObj);
  } else {
    console.log(`AUTOSAVE_LOG [${category}]:`, logObj);
  }
}

/**
 * Always call React Hooks in the same order.
 * Even if props is null, we define all Hooks at the top level.
 * Then inside each effect or callback, we check if props is valid and do nothing otherwise.
 */
export function useAutosave(props: UseAutosaveProps | null): UseAutosaveReturn {
  const [savingStatus, setSavingStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [dirty, setDirty] = useState(false);
  const lastDataRef = useRef<Record<string, any>>({});
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const saveCountRef = useRef(0);
  const pendingSaveRef = useRef<Promise<void> | null>(null);
  const lastSaveAttemptTime = useRef<number>(0);
  const MIN_TIME_BETWEEN_SAVES = 5000; // 5 seconds

  // LocalStorage key if props exists, otherwise empty
  const localStorageKey = props ? `autosave_${props.submissionId}` : '';

  // Add new ref to track save attempt lifecycle
  const saveAttemptStateRef = useRef<{
    startTime: number;
    lastRetryTime: number;
    retryCount: number;
    lastError?: Error;
  }>({
    startTime: 0,
    lastRetryTime: 0,
    retryCount: 0,
    lastError: undefined
  });

  // Add a ref to track if we're in backoff mode
  const backoffModeRef = useRef<{
    active: boolean;
    nextAttemptTime: number;
  }>({
    active: false,
    nextAttemptTime: 0
  });

  // Add debug logging for save lifecycle
  const logSaveAttempt = (action: string, details: Record<string, any>) => {
    const level = action.includes('error') || action === 'timeout' || action === 'max_retries' ? 'error' : 'log';
    
    logAutosave(level, 'lifecycle', {
      action,
      formId: props?.submissionId,
      timeSinceStart: Date.now() - saveAttemptStateRef.current.startTime,
      retryCount: saveAttemptStateRef.current.retryCount,
      ...details
    });
  };

  // Reset save attempt state
  const resetSaveAttempt = () => {
    saveAttemptStateRef.current = {
      startTime: Date.now(),
      lastRetryTime: 0,
      retryCount: 0,
      lastError: undefined
    };
    logSaveAttempt('reset', { saveCount: saveCountRef.current });
  };

  // Debug dirty state changes
  useEffect(() => {
    logAutosave('log', 'dirty-state', {
      isDirty: dirty,
      submissionId: props?.submissionId,
      saveCount: saveCountRef.current,
      lastDataKeys: Object.keys(lastDataRef.current),
      currentDataKeys: props?.formData ? Object.keys(props.formData) : [],
      timeSinceLastSave: Date.now() - lastSaveAttemptTime.current,
      hasPendingSave: !!pendingSaveRef.current
    });
  }, [dirty, props?.formData]);

  /**
   * On first mount, try to load any cached data from localStorage and merge it.
   * Only do anything if props is not null.
   */
  useEffect(() => {
    if (!props) return; // no-op if missing props
    try {
      const cached = localStorage.getItem(localStorageKey);
      logAutosave('log', 'init', {
        submissionId: props.submissionId,
        hasCachedData: !!cached,
        currentData: props.formData ? Object.keys(props.formData).length : 0
      });
      
      if (cached) {
        // For demonstration, we just mark dirty so next save includes it
        setDirty(true);
      }
    } catch (error) {
      // ignore localStorage errors
      logAutosave('error', 'localStorage-error', {
        submissionId: props.submissionId,
        error: String(error)
      });
    }
  }, [props, localStorageKey]);

  /**
   * Periodic autosave every 30 seconds if there are unsaved changes.
   */
  useEffect(() => {
    if (!props) return;
    const intervalId = setInterval(() => {
      if (dirty && !backoffModeRef.current.active) {
        logAutosave('log', 'interval-trigger', {
          submissionId: props.submissionId,
          isDirty: dirty,
          backoffMode: backoffModeRef.current.active
        });
        doSaveToServer();
      }
    }, 30000);

    return () => clearInterval(intervalId);
    // We include "dirty" in the dependency array
  }, [props, dirty]);

  /**
   * Compare the incoming form data with the last saved data to see if it's "dirty."
   * If there's any difference, we set dirty = true and start/reset the 2s debounce.
   */
  useEffect(() => {
    if (!props) return;

    const formDataChanged = !shallowEqual(props.formData, lastDataRef.current);
    logAutosave('log', 'data-comparison', {
      formDataChanged,
      submissionId: props.submissionId,
      currentDataSize: JSON.stringify(props.formData).length,
      lastDataSize: JSON.stringify(lastDataRef.current).length,
      currentDataKeys: Object.keys(props.formData).length,
      lastDataKeys: Object.keys(lastDataRef.current).length,
      isDirty: dirty,
      saveCount: saveCountRef.current
    });

    if (formDataChanged) {
      setDirty(true);
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      
      logAutosave('log', 'debounce-start', {
        submissionId: props.submissionId,
        delay: 2000
      });
      
      debounceTimer.current = setTimeout(() => {
        logAutosave('log', 'debounce-complete', {
          submissionId: props.submissionId
        });
        doSaveToServer();
      }, 2000);
    }
  }, [props]);

  /**
   * The immediate autosave function, used when a radio button or dropdown changes:
   * This bypasses the 2s debounce and saves right away.
   */
  function handleImmediateAutosave() {
    if (!props) return; // no-op
    
    logAutosave('log', 'immediate-save-requested', {
      submissionId: props?.submissionId,
      isDirty: dirty,
      hasPendingSave: !!pendingSaveRef.current,
      hasPendingDebounce: !!debounceTimer.current
    });
    
    // Clear any pending debounced call
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    doSaveToServer();
  }

  /**
   * Cancel any pending operations on unmount
   */
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      
      logAutosave('log', 'cleanup', {
        submissionId: props?.submissionId,
        isDirty: dirty,
        hasPendingSave: !!pendingSaveRef.current
      });
    };
  }, []);

  // Add max retries constant
  const MAX_RETRIES = 3;
  const TIMEOUT_MS = 10000;

  /**
   * Main function that does the actual server save if dirty and if props exist.
   */
  async function doSaveToServer() {
    const now = Date.now();
    
    // Validate props exist before attempting save
    if (!props) {
      logAutosave('warn', 'save-abort', {
        reason: 'missing-props'
      });
      return;
    }

    // If we're in backoff mode and it's not time yet, skip this save
    if (backoffModeRef.current.active && now < backoffModeRef.current.nextAttemptTime) {
      logSaveAttempt('skip_backoff', {
        timeUntilNextAttempt: backoffModeRef.current.nextAttemptTime - now,
        retryCount: saveAttemptStateRef.current.retryCount
      });
      return;
    }

    const timeSinceLastSave = now - lastSaveAttemptTime.current;

    logSaveAttempt('attempt', {
      timeSinceLastSave,
      isDirty: dirty,
      hasPendingSave: !!pendingSaveRef.current,
      formDataKeys: props?.formData ? Object.keys(props.formData).length : 0,
      dataSize: JSON.stringify(props.formData).length,
      inBackoffMode: backoffModeRef.current.active
    });

    // Don't retry if we've hit the max
    if (saveAttemptStateRef.current.retryCount >= MAX_RETRIES) {
      logSaveAttempt('max_retries', {
        retryCount: saveAttemptStateRef.current.retryCount,
        lastError: saveAttemptStateRef.current.lastError
      });
      // Reset retry count but keep dirty state
      resetSaveAttempt();
      backoffModeRef.current = { active: false, nextAttemptTime: 0 };
      return;
    }

    // Don't save if we haven't waited long enough
    if (timeSinceLastSave < MIN_TIME_BETWEEN_SAVES) {
      logSaveAttempt('skip_time', {
        timeSinceLastSave,
        minRequired: MIN_TIME_BETWEEN_SAVES
      });
      
      // Calculate next attempt time with exponential backoff
      const retryDelay = Math.min(
        MIN_TIME_BETWEEN_SAVES * Math.pow(2, saveAttemptStateRef.current.retryCount),
        30000 // Max 30 second delay
      );
      
      // Enter backoff mode
      backoffModeRef.current = {
        active: true,
        nextAttemptTime: now + retryDelay
      };

      logSaveAttempt('enter_backoff', { 
        retryDelay,
        nextAttemptTime: backoffModeRef.current.nextAttemptTime,
        retryCount: saveAttemptStateRef.current.retryCount 
      });
      
      return;
    }

    // Clear any existing pending save reference
    if (pendingSaveRef.current) {
      logAutosave('warn', 'clear-pending', {
        submissionId: props.submissionId,
        saveCount: saveCountRef.current
      });
      pendingSaveRef.current = null;
    }

    // Start new save attempt
    lastSaveAttemptTime.current = now;
    setSavingStatus('saving');
    saveCountRef.current += 1;
    const currentSaveCount = saveCountRef.current;

    try {
      // Detailed log of what we're about to save
      logAutosave('log', 'save-payload', {
        submissionId: props.submissionId,
        userId: props.userId,
        saveCount: currentSaveCount,
        dataSize: JSON.stringify(props.formData).length,
        dataKeys: Object.keys(props.formData)
      });
      
      // Create both promises before starting the race
      const savePromise = autosaveSubmission({
        submissionId: props.submissionId,
        userId: props.userId,
        createdAt: props.createdAt,
        submittedAt: props.isSubmitted ? new Date().toISOString() : null,
        fields: props.formData,
      });

      // Type the timeout promise to return void to match autosaveSubmission
      const timeoutPromise = new Promise<void>((_, reject) => {
        setTimeout(() => {
          const timeoutError = new Error(`Save timeout after ${TIMEOUT_MS}ms`);
          logSaveAttempt('timeout', { 
            saveCount: currentSaveCount,
            pendingSaveExists: !!pendingSaveRef.current,
            submissionId: props.submissionId
          });
          reject(timeoutError);
        }, TIMEOUT_MS);
      });

      // Now Promise.race will return Promise<void>
      pendingSaveRef.current = Promise.race([savePromise, timeoutPromise]);
      
      logAutosave('log', 'save-waiting', {
        submissionId: props.submissionId,
        saveCount: currentSaveCount,
        timeout: TIMEOUT_MS
      });
      
      await pendingSaveRef.current;

      if (currentSaveCount === saveCountRef.current) {
        logSaveAttempt('success', {
          saveCount: currentSaveCount,
          saveTime: Date.now() - now,
          submissionId: props.submissionId
        });

        lastDataRef.current = {...props.formData};
        setDirty(false);
        setSavingStatus('saved');
        resetSaveAttempt();
        // Exit backoff mode on success
        backoffModeRef.current = { active: false, nextAttemptTime: 0 };
      }
    } catch (err) {
      const errorObj = err instanceof Error ? 
        { message: err.message, name: err.name, stack: err.stack } : 
        String(err);
      
      logSaveAttempt('error', {
        saveCount: currentSaveCount,
        submissionId: props.submissionId,
        error: errorObj,
        timeSinceStart: Date.now() - now,
        willRetry: saveAttemptStateRef.current.retryCount < MAX_RETRIES
      });
      
      if (currentSaveCount === saveCountRef.current) {
        setSavingStatus('error');
        saveAttemptStateRef.current.lastError = err as Error;
        saveAttemptStateRef.current.retryCount++;
      }
    } finally {
      pendingSaveRef.current = null;
      
      logSaveAttempt('complete', {
        saveCount: currentSaveCount,
        status: savingStatus,
        elapsed: Date.now() - saveAttemptStateRef.current.startTime,
        retryCount: saveAttemptStateRef.current.retryCount,
        submissionId: props.submissionId
      });
    }
  }

  return {
    savingStatus,
    handleImmediateAutosave,
  };
}

/**
 * Shallow equality check of the "fields" object to reduce redundant saves.
 */
function shallowEqual(objA: Record<string, any>, objB: Record<string, any>): boolean {
  const aKeys = Object.keys(objA);
  const bKeys = Object.keys(objB);
  if (aKeys.length !== bKeys.length) return false;

  for (const key of aKeys) {
    if (objA[key] !== objB[key]) {
      return false;
    }
  }
  return true;
}
